// Runtime config — fetched once at boot from /api/config (Node server reads .env).
// Keeping config out of the static build means we can flip testnet ↔ mainnet
// (and mock ↔ real tokens) by editing .env, no rebuild.

export type Network = "testnet" | "devnet" | "mainnet";

/**
 * Boot-time config from /api/config. Intentionally minimal — everything else
 * (contract owner, registered pairs, their tokens, fees, etc.) is queried
 * from the contract itself at runtime so the deployed contract is the single
 * source of truth.
 */
export type AppConfig = {
  network: Network;
  contractAddress: string;
  /** Optional. If set, enables the xPortal (WalletConnect) option in the
   *  wallet picker. Register at https://cloud.walletconnect.com to get one. */
  walletConnectProjectId?: string;
  /** Optional. Address of the NFT-Liquidity contract. When non-empty, the
   *  dApp shows the "HATOM NFTs" tab; when empty, only the Staked-Tokens
   *  surface is rendered. */
  nftLiquidityContractAddress?: string;
};

export const NETWORK_URLS: Record<Network, { api: string; gateway: string; explorer: string }> = {
  mainnet: {
    api:      "https://api.multiversx.com",
    gateway:  "https://gateway.multiversx.com",
    explorer: "https://explorer.multiversx.com",
  },
  testnet: {
    api:      "https://testnet-api.multiversx.com",
    gateway:  "https://testnet-gateway.multiversx.com",
    explorer: "https://testnet-explorer.multiversx.com",
  },
  devnet: {
    api:      "https://devnet-api.multiversx.com",
    gateway:  "https://devnet-gateway.multiversx.com",
    explorer: "https://devnet-explorer.multiversx.com",
  },
};

export async function loadConfig(): Promise<AppConfig> {
  const resp = await fetch("/api/config");
  if (!resp.ok) throw new Error(`/api/config returned HTTP ${resp.status}`);
  return (await resp.json()) as AppConfig;
}
